import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Handshake, Trophy, Users } from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * "🤝 Brokers who work with you" card for /app/loadboard/analytics.
 * Groups bid history by broker, shows top 5 by win-rate (min 2 closed bids).
 * Falls back to sorting by total bids if user hasn't closed enough yet.
 * Hides entirely when the user hasn't bid on any broker yet.
 */
export default function BrokerRelationshipsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    client.get("/loadboard/analytics/brokers?limit=5")
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && toast.error(e?.response?.data?.detail || "Broker analytics failed"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="card-surface p-6" data-testid="brokers-card-loading">
        <div className="animate-pulse text-neutral-500 text-sm">Loading broker relationships…</div>
      </div>
    );
  }
  if (!data || data.total_unique_brokers === 0) return null;

  const showingUnqualified = data.qualified_broker_count === 0;

  return (
    <div className="card-surface p-6" data-testid="brokers-card">
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#FF5A1F] mb-1">
            <Handshake size={13} weight="bold" />
            BROKER RELATIONSHIPS
          </div>
          <h3 className="font-display text-lg font-semibold text-white">
            Who's working with you
          </h3>
          <p className="text-[11px] text-neutral-500 mt-1 max-w-md">
            {showingUnqualified
              ? "Close more bids to see win-rate rankings. Ranked by total bids until you close ≥2."
              : `Ranked by win rate. ${data.qualified_broker_count} broker${data.qualified_broker_count === 1 ? "" : "s"} qualified (≥2 closed bids).`}
          </p>
        </div>
        <div className="inline-flex items-center gap-1.5 text-[11px] text-neutral-400 shrink-0">
          <Users size={12} />
          {data.total_unique_brokers} total broker{data.total_unique_brokers === 1 ? "" : "s"}
        </div>
      </div>

      <div className="space-y-2" data-testid="brokers-list">
        {data.top_brokers.map((b, i) => (
          <BrokerRow key={b.broker_company} broker={b} rank={i + 1} showingUnqualified={showingUnqualified} />
        ))}
      </div>
    </div>
  );
}


function BrokerRow({ broker, rank, showingUnqualified }) {
  const wr = broker.win_rate_pct;
  // Tone the win-rate badge based on how good the number is
  const wrTone = wr === null
    ? "text-neutral-500 bg-white/[0.02] border-white/10"
    : wr >= 75
      ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/30"
      : wr >= 40
        ? "text-[#FF5A1F] bg-[#FF5A1F]/10 border-[#FF5A1F]/30"
        : "text-amber-400 bg-amber-500/10 border-amber-500/30";

  return (
    <div
      className="flex items-center gap-4 py-2.5 px-3 rounded-lg hover:bg-white/[0.02] transition-colors"
      data-testid={`broker-row-${broker.broker_company.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div className="w-5 text-[11px] text-neutral-500 font-mono text-center shrink-0">
        {rank === 1 ? <Trophy size={13} weight="fill" className="text-[#FF5A1F] mx-auto" /> : `#${rank}`}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white font-medium truncate">{broker.broker_company}</div>
        <div className="text-[11px] text-neutral-500 mt-0.5">
          {broker.total_bids} bid{broker.total_bids === 1 ? "" : "s"}
          {broker.closed > 0 && ` · ${broker.won}W ${broker.lost}L`}
          {broker.avg_won_per_mile > 0 && ` · $${broker.avg_won_per_mile.toFixed(2)}/mi avg won`}
        </div>
      </div>
      <div className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold tabular-nums ${wrTone}`}>
        {wr !== null ? `${wr}%` : (showingUnqualified ? `${broker.total_bids} bids` : "—")}
      </div>
    </div>
  );
}
